import React, { useEffect, useState, useMemo, useCallback } from 'react';
/**
 * ServiceUserDashboard
 * --------------------
 * Purpose:
 *   - Shows a tabbed view of referrals (patients/CYP) the current user is associated with
 *     in the current organisation.
 *   - Two tabs: "Pending Referrals" (forms incomplete) and "Completed Referrals" (all forms done).
 *   - Displays School and Guardian form completion status for each referral.
 *   - Provides a CTA that navigates to the patient overview page for a selected referral.
 *
 * Implementation:
 *   - Data is fetched from the backend (rmsListUserReferrals) to respect tenancy and auth.
 *   - For each referral, we read the patient record's formProgress mirror to
 *     determine completion status (client formInstances reads are provider-only).
 *   - We also fetch participants to determine if a guardian has been assigned.
 *   - Status values: Not Assigned | Not Started | Started | Completed
 */
import providerLogo from '../assets/images/logos/lcs_logo.png';
import { auth, db } from '../../firebase';
import { Navigate, useNavigate } from 'react-router-dom';
import { isHarbourSencoPortalEnabled, isHarbourFamilyPortalEnabled } from '../constants/orgFeatures';
import { useAuth } from '../../hooks/useAuth';
import useOrgHfpInstrument from '../hooks/useOrgHfpInstrument';
import { useRmsState } from '../context/RmsStateContext';
import { getOrgConfig } from '../utils/orgResolution';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { InformationCircleIcon, ClipboardCopyIcon, CalendarIcon, MailIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import { formatDateShort } from '../../utils/timestampUtils';
import { apiGetReferralParticipants, apiResendParticipantInvite } from '../utils/rmsApi';
import {
  STATUS,
  calculateRoleStatus,
  calculateSenRoleStatus,
  getStatusBadgeConfig,
  getTriageFormsProgress,
  isRoleReferralFormsComplete,
  areRoleSenFormsComplete,
  isSenOnlyRecord,
  isHarbourPupilRecord,
  buildFormProgressFromRecord,
} from '../utils/referralStatus';
import { PENDING_PARTICIPANT_STATUSES, PARTICIPANT_ROLE } from '../../constants/participantStatuses';

// NOTE:
// Status/form-key logic is centralised in `src/rms/utils/referralStatus.js` so that:
// - The dashboard table
// - The "People involved" popup (PeoplePanel)
// remain in parity when form keys evolve (e.g. SSN -> HFP migration).

export default function ServiceUserDashboard() {
  const navigate = useNavigate();
  const { claims } = useAuth();
  // Primary: use rmsOrgId claim if already set (returning users).
  // Fallback: resolve from URL ?orgId= param or hostname mapping (first-time
  // users arriving via invite email before lazy linking sets their claims).
  // Mirrors the pattern used by CompleteInvite.js and ServiceUserSignIn.js.
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  // The organisation's HFP instrument setting (TDI-523), for pupils whose
  // profile has not started; a started profile carries its own pin.
  const orgHfpInstrument = useOrgHfpInstrument(orgId);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Destructure stable useCallback references from the RMS context rather than
  // holding the whole context object. The context value includes a `versionTick`
  // counter that increments on every cache mutation (bump()), so using the full
  // object as a useEffect dependency would re-fire effects on every cache update,
  // creating an infinite render loop (warmRms → bump → rmsState changes → warmRms …).
  const { getCachedReferrals, ensureReferrals, warmRms } = useRmsState();
  const [rows, setRows] = useState(() => getCachedReferrals(orgId));
  const [refTypeByPatientId, setRefTypeByPatientId] = useState({});
  // schoolPhase per patient ('primary' | 'secondary'), read alongside referrerType.
  // Secondary referrals skip the HFP, so status/completion must exclude it.
  const [schoolPhaseByPatientId, setSchoolPhaseByPatientId] = useState({});
  // intakeConcern per patient ('asd' | 'adhd' | 'both'); asd-only referrals skip
  // the ADHD SNAP-IV scale, so status/completion must exclude it (TD-139).
  const [intakeConcernByPatientId, setIntakeConcernByPatientId] = useState({});
  // Harbour SEN-only pupils (no referral on the record): completion and
  // badges run on the HFP forms alone (TDI-53).
  const [senOnlyByPatientId, setSenOnlyByPatientId] = useState({});
  // The fields the status helpers need to pick a record's HFP set
  // (hfpFormKeysForRecord): its stored instrument (TDI-414) and the Harbour
  // flag the legacy rule falls back on. A minimal record, never the full doc.
  const [hfpRecordByPatientId, setHfpRecordByPatientId] = useState({});
  
  // Tab state: 'pending' or 'completed'
  const [activeTab, setActiveTab] = useState('pending');
  
  // Form completion data per patient: { [patientRecordId]: { formProgress: {...}, triageAssigned: bool, hasGuardian: bool } }
  const [completionData, setCompletionData] = useState({});
  const [completionLoading, setCompletionLoading] = useState(false);
  
  // Current user's role derived from custom claims. Seeded synchronously
  // from the auth hook's claims so a linked parent or SENCO redirects to
  // their Harbour portal on first paint, without flashing this legacy
  // dashboard (staging E2E feedback, 26 Oct); the effect below keeps it
  // fresh as claims land or change.
  const roleFromUserType = (userType) => {
    const ut = String(userType || '').toLowerCase();
    if (ut.includes('school')) return 'school_user';
    if (ut.includes('guardian')) return 'guardian';
    // TDI-489: a class teacher has their own task list; this dashboard lists
    // referrals they must never see.
    if (ut.includes('teacher')) return 'teacher';
    return null;
  };
  const [userRole, setUserRole] = useState(() => roleFromUserType(claims?.userType)); // 'school_user' | 'guardian' | null

  // Fetch current user's role from custom claims. Re-derived whenever the
  // auth hook sees fresh claims: a brand-new parent or carer has no userType
  // until lazy linking sets it (the referrals fetch below forces the token
  // refresh), and the old one-shot read stranded them on this legacy
  // dashboard instead of their Harbour portal until a full reload
  // (staging E2E feedback, 26 Oct).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = auth?.currentUser;
        if (!u) return;
        const t = await u.getIdTokenResult(true);
        if (!cancelled) setUserRole(roleFromUserType(t?.claims?.userType));
      } catch (_) {
        // no-op
      }
    })();
    return () => { cancelled = true; };
  }, [claims?.userType]);
  
  // Fetch referrals list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const cached = getCachedReferrals(orgId);
        if (!cancelled && cached && cached.length) {
          setRows(cached);
        }
        // When claims are not yet set (first-time user via invite link), bypass
        // the 30s cache so the backend's lazy-linking logic always runs.
        const fresh = await ensureReferrals({ orgId, force: !claimsOrgId });
        if (!cancelled) setRows(fresh || []);

        // If claims were absent (bootstrap scenario) and the backend returned
        // referrals, lazy linking has set custom claims server-side. Force a
        // token refresh so the client JWT reflects the new claims — required
        // for Firestore security rules on subsequent reads. Mirrors the
        // pattern in CompleteInvite.js after apiCompleteReferrerAccountSetup.
        if (!cancelled && !claimsOrgId && fresh?.length) {
          await auth.currentUser?.getIdToken(true);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load referrals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, claimsOrgId, getCachedReferrals, ensureReferrals]);

  // Pre-warm common RMS form schemas so subsequent navigation feels instant.
  // warmRms is a stable useCallback, so this fires once per orgId (not on every cache bump).
  useEffect(() => {
    warmRms({ orgId, formKeys: ['requestForReferral', 'educationHistory', 'reasonForReferral'] });    
  }, [orgId, warmRms]);

  // Enrich rows with referrerType from patientRecords.
  // Uses the functional updater form of setRefTypeByPatientId so the effect
  // doesn't depend on the previous state (avoids a dependency cycle where
  // the effect both reads and writes the same state).
  const enrichRowsWithReferrerType = useCallback(async (currentOrgId, currentRows, signal) => {
    if (!currentOrgId || !currentRows || currentRows.length === 0) return;
    const allPatientIds = currentRows.map(r => r?.patientRecordId).filter(Boolean);
    if (allPatientIds.length === 0) return;
    const results = await Promise.all(allPatientIds.map(async (id) => {
      try {
        const pr = await getDoc(doc(db, 'rms_organisations', currentOrgId, 'patientRecords', id));
        const data = pr.exists() ? (pr.data() || {}) : {};
        const rt = String(data.referrerType || '').toLowerCase().trim();
        const phase = data.schoolPhase || data.school?.schoolType || null;
        const concern = data.intakeConcern || null;
        // Harbour SEN-only pupils (TDI-53): the HFP forms are the whole ask,
        // so completion must not wait on referral forms nobody was given.
        const senOnly = isSenOnlyRecord(data);
        const hfpRecord = {
          harbour: isHarbourPupilRecord(data) ? { pupil: true } : null,
          hfpInstrument: data.hfpInstrument || null,
        };
        return [id, rt, phase, concern, senOnly, hfpRecord];
      } catch (_e) {
        return [id, '', null, null, false, null];
      }
    }));
    if (signal.cancelled) return;
    setRefTypeByPatientId((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach(([id, rt]) => {
        if (!(id in prev)) {
          next[id] = rt;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSchoolPhaseByPatientId((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach(([id, , phase]) => {
        if (!(id in prev)) {
          next[id] = phase;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setIntakeConcernByPatientId((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach(([id, , , concern]) => {
        if (!(id in prev)) {
          next[id] = concern;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSenOnlyByPatientId((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach(([id, , , , senOnly]) => {
        if (!(id in prev)) {
          next[id] = senOnly;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setHfpRecordByPatientId((prev) => {
      let changed = false;
      const next = { ...prev };
      results.forEach(([id, , , , , hfpRecord]) => {
        if (!(id in prev)) {
          next[id] = hfpRecord;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    enrichRowsWithReferrerType(orgId, rows, signal).catch(() => { /* soft-fail */ });
    return () => { signal.cancelled = true; };
  }, [orgId, rows, enrichRowsWithReferrerType]);

  /**
   * Real-time listener for form completion data across all patients.
   *
   * Uses onSnapshot on each patient record's formProgress mirror (TDI-154) to
   * get instant updates when forms are completed (e.g., via auto-complete).
   * Client reads of the formInstances subcollection are provider-only; the
   * backend mirrors each instance's headline status/percent onto the record,
   * which participants can read.
   *
   * Also fetches participant data once to determine guardian status.
   */
  // Key the listener effect on the patient-id SET, not the `rows` array
  // reference — `rows` gets a fresh reference several times during load
  // (cache, then network) and each change tore down and re-subscribed every
  // listener, delaying stable completion data.
  const patientIdsKey = useMemo(
    () => (rows || []).map(r => r?.patientRecordId).filter(Boolean).join(','),
    [rows],
  );
  useEffect(() => {
    if (!orgId || !patientIdsKey || !userRole) return undefined;

    setCompletionLoading(true);
    const patientIds = patientIdsKey.split(',');
    
    // Store unsubscribe functions for cleanup
    const unsubscribes = [];
    
    // Track participant data separately (fetched once, not real-time)
    const participantDataRef = {};
    
    // Fetch participant data for all patients once (guardian status, invite status)
    // This is not real-time because participant changes are less frequent
    Promise.all(patientIds.map(async (patientRecordId) => {
      try {
        const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
        // Filter out 'superseded' participants - these are old nominations that were
        // replaced when the school user changed the guardian's email before invite was consumed
        // RMS guardian participant (PARTICIPANT_ROLE.GUARDIAN — the RMS Legal
        // Guardian, NOT the Assessment-platform guardian).
        const guardianParticipant = Array.isArray(participants)
          ? participants.find(p => p.role === PARTICIPANT_ROLE.GUARDIAN && p.status !== 'superseded')
          : null;
        // The participant the current user can send a reminder to: a still-pending
        // RMS nominee of the OTHER role (school user reminds the guardian; guardian
        // reminds the school representative). Resending only makes sense while
        // they have not yet linked their account.
        const otherRole = userRole === PARTICIPANT_ROLE.SCHOOL_USER
          ? PARTICIPANT_ROLE.GUARDIAN
          : PARTICIPANT_ROLE.SCHOOL_USER;
        const reminderTarget = Array.isArray(participants)
          ? participants.find(p => p.role === otherRole && p.status !== 'superseded' && PENDING_PARTICIPANT_STATUSES.includes(p.status))
          : null;
        participantDataRef[patientRecordId] = {
          hasGuardian: !!guardianParticipant,
          guardianInviteStatus: guardianParticipant?.status || null,
          reminderTargetId: reminderTarget?.id || null,
          reminderTargetRole: reminderTarget?.role || null,
        };
      } catch (_) {
        participantDataRef[patientRecordId] = { hasGuardian: false, guardianInviteStatus: null, reminderTargetId: null, reminderTargetRole: null };
      }
    })).then(() => {
      // Set up real-time listeners on each patient record (formProgress mirror).
      //
      // Completion data counts as loaded only once EVERY patient's first
      // snapshot has delivered — flipping the flag at subscription time let
      // the table render from an empty completionData map, so the other
      // party's forms read "Not started" on the first dashboard paint even
      // when they were complete (they corrected only after navigating away
      // and back, once Firestore had the docs cached).
      const pendingFirstSnapshot = new Set(patientIds);
      const markDelivered = (patientRecordId) => {
        pendingFirstSnapshot.delete(patientRecordId);
        if (pendingFirstSnapshot.size === 0) setCompletionLoading(false);
      };
      patientIds.forEach((patientRecordId) => {
        const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);

        const unsubscribe = onSnapshot(prRef, (snap) => {
          try {
            // Shared fold applies the completionPercent invariant (submitted =
            // 100 even with a missing/stale mirrored percent) so the other
            // party's status never reads "Not Started" after they have
            // submitted. A record without the mirror (pre-backfill) folds to
            // an empty map — every form reads as not started.
            const record = snap.exists() ? (snap.data() || {}) : {};
            const { formProgress, triageAssigned } = buildFormProgressFromRecord(record);

            // Merge with participant data
            const participantData = participantDataRef[patientRecordId] || {};

            // Update state for this specific patient
            setCompletionData(prev => ({
              ...prev,
              [patientRecordId]: {
                formProgress,
                triageAssigned,
                hasGuardian: participantData.hasGuardian || Object.keys(formProgress).some(k => k.endsWith('_guardian')),
                guardianInviteStatus: participantData.guardianInviteStatus,
                reminderTargetId: participantData.reminderTargetId || null,
                reminderTargetRole: participantData.reminderTargetRole || null,
              }
            }));
          } catch (_e) {
            // Soft-fail on parse errors
          }
          markDelivered(patientRecordId);
        }, (_err) => {
          // Soft-fail on listener errors — still counts as delivered so one
          // failing patient cannot hold the whole dashboard on the skeleton.
          markDelivered(patientRecordId);
        });

        unsubscribes.push(unsubscribe);
      });
    }).catch(() => {
      setCompletionLoading(false);
    });
    
    // Cleanup: unsubscribe all listeners
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [orgId, patientIdsKey, userRole]);

  /**
   * Calculate the form completion status for a given role
   * Returns: NOT_ASSIGNED | INVITE_SENT | NOT_STARTED | STARTED | COMPLETED
   * 
   * For guardians, the flow is:
   * - No guardian nominated → NOT_ASSIGNED
   * - Guardian nominated, invite sent but not consumed → INVITE_SENT
   * - Guardian has linked account → check form progress (NOT_STARTED/STARTED/COMPLETED)
   */
  const getRoleStatus = useMemo(() => {
    return (patientRecordId, role) => {
      const data = completionData[patientRecordId];
      if (!data) return STATUS.NOT_STARTED;
      
      const { formProgress, triageAssigned, hasGuardian, guardianInviteStatus } = data;
      const patient = hfpRecordByPatientId[patientRecordId] || null;

      // Harbour SEN-only pupils: status over the HFP forms alone, so the row
      // can honestly reach "Completed" without referral forms.
      if (senOnlyByPatientId[patientRecordId]) {
        return calculateSenRoleStatus({
          role, formProgress, hasGuardian, guardianInviteStatus, patient, orgSetting: orgHfpInstrument,
        });
      }

      return calculateRoleStatus({
        role,
        formProgress,
        triageAssigned,
        hasGuardian,
        guardianInviteStatus,
        schoolPhase: schoolPhaseByPatientId[patientRecordId] || null,
        intakeConcern: intakeConcernByPatientId[patientRecordId] || null,
        patient,
      });
    };
  }, [completionData, schoolPhaseByPatientId, intakeConcernByPatientId, senOnlyByPatientId, hfpRecordByPatientId, orgHfpInstrument]);

  /**
   * Determine if a referral is "complete" for the current user
   * 
   * IMPORTANT: For dashboard classification (Pending vs Completed tabs), we ONLY
   * consider the core referral + HFP forms, NOT triage forms.
   * 
   * Rationale:
   *   - Triage forms are assigned AFTER the guardian completes referral + HFP forms
   *   - If we included triage forms in completion status, a guardian who finished
   *     all their referral + HFP forms would suddenly move from "Completed" back to
   *     "Pending" when triage forms are assigned (at 0% progress)
   *   - This would be confusing UX - the user completed their initial work but now
   *     sees it as "incomplete" just because additional optional forms were added
   * 
   * The status badges in the table still use calculateRoleStatus (which includes
   * triage forms when assigned), so users can see if they have more forms to fill.
   * But the tab classification uses only referral + HFP completion.
   */
  const isReferralComplete = useMemo(() => {
    return (patientRecordId) => {
      if (!userRole) return false;
      
      // For guardians, we need to check invite status first
      // (can't be "complete" if invite hasn't been consumed yet)
      const data = completionData[patientRecordId];
      if (userRole === 'guardian') {
        if (!data?.hasGuardian) return false;
        const inviteStatus = data?.guardianInviteStatus;
        if (PENDING_PARTICIPANT_STATUSES.includes(inviteStatus)) {
          return false;
        }
      }
      
      // isRoleReferralFormsComplete excludes the triage forms.
      const formProgress = data?.formProgress || {};
      const patient = hfpRecordByPatientId[patientRecordId] || null;
      // Harbour SEN-only pupils complete on the HFP forms alone.
      if (senOnlyByPatientId[patientRecordId]) {
        return areRoleSenFormsComplete(userRole, formProgress, patient, orgHfpInstrument);
      }
      return isRoleReferralFormsComplete(
        userRole,
        formProgress,
        intakeConcernByPatientId[patientRecordId] || null,
      );
    };
  }, [userRole, completionData, schoolPhaseByPatientId, intakeConcernByPatientId, senOnlyByPatientId, hfpRecordByPatientId, orgHfpInstrument]);

  /**
   * Split rows into pending and completed based on current user's form completion
   */
  const { pendingRows, completedRows } = useMemo(() => {
    const pending = [];
    const completed = [];
    
    for (const row of rows) {
      const patientRecordId = row?.patientRecordId;
      if (!patientRecordId) continue;
      
      if (isReferralComplete(patientRecordId)) {
        completed.push(row);
      } else {
        pending.push(row);
      }
    }
    
    return { pendingRows: pending, completedRows: completed };
  }, [rows, isReferralComplete]);

  // Select rows based on active tab
  const displayRows = activeTab === 'pending' ? pendingRows : completedRows;

  // TDI-489: teachers land on their own task list, wherever they signed in.
  if (userRole === 'teacher') {
    return <Navigate to="/rms/teacher" replace />;
  }

  // Harbour SENCo portal (TDI-53): school users at onboarded organisations
  // get the redesigned portal; guardians keep this dashboard.
  if (userRole === 'school_user' && isHarbourSencoPortalEnabled(orgId)) {
    return <Navigate to="/rms/pupils" replace />;
  }

  // Harbour family portal (TDI-53): guardians at onboarded organisations get
  // the redesigned parent-or-carer portal.
  if (userRole === 'guardian' && isHarbourFamilyPortalEnabled(orgId)) {
    return <Navigate to="/rms/family/children" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      {/* RMS header shell with provider logo */}
      <div className="sticky top-0 left-0 right-0 bg-white z-30" style={{ boxShadow: '0 24px 36px rgba(255,255,255,0.95), 0 12px 20px rgba(255,255,255,0.85)' }}>
        <div className="w-full px-6 md:px-10 pt-10">
          <div className="w-full md:w-2/3 lg:w-3/4 xl:w-2/3 mx-auto mb-8 flex items-center justify-between gap-2 relative">
            {/* Burger menu */}
            <div className="relative">
              <button
                type="button"
                aria-label="Open menu"
                className="p-2 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                onClick={() => setMenuOpen(o => !o)}
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {/* Dropdown */}
              <div className={`absolute left-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden transition-all duration-200 ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'}`}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={async () => {
                    try { await auth.signOut(); } catch (_) {}
                    navigate('/rms/sign-in?signedout=1', { replace: true });
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
            <img src={providerLogo} alt="LCS logo" className="h-20 w-auto opacity-90" />
          </div>
          {/* Page title (centered), displayed below brand row and above main content */}
          <div className="w-full md:w-2/3 lg:w-3/4 xl:w-2/3 mx-auto mb-4 text-center">
            <h1 className="text-xl md:text-3xl font-bold text-gray-900">
              ADHD and Autism Referral Dashboard
            </h1>
          </div>
        </div>
      </div>

      <div className="h-12" />
      <div className="flex-grow w-full px-6 md:px-10 pb-28 text-left">
        <div className="w-full md:w-2/3 lg:w-3/4 xl:w-2/3 mx-auto">
          {/* Header row with title and new referral button */}
          {/* New referral button is only shown to school users, not guardians */}
          <div className="flex items-center justify-between mb-4 mt-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Referrals</h2>
            {userRole === 'school_user' && (
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-primary text-primary hover:bg-primary/5 focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                onClick={() => navigate('/rms/referral/new')}
              >
                <span className="text-sm font-semibold">+ Start New Referral</span>
              </button>
            )}
          </div>

          {/* Tab navigation - Pending / Completed */}
          <div className="mb-4">
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'pending'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('pending')}
              >
                Your Pending Referrals
                {!loading && !completionLoading && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                    {pendingRows.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'completed'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('completed')}
              >
                Your Completed Referrals
                {!loading && !completionLoading && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                    {completedRows.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Loading state - skeleton cards instead of text for better UX */}
          {(loading || completionLoading) && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 animate-pulse">
                  <div className="hidden md:grid grid-cols-12 items-center gap-2">
                    <div className="col-span-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                    <div className="col-span-3 flex justify-end">
                      <div className="h-8 bg-gray-200 rounded w-28"></div>
                    </div>
                  </div>
                  {/* Mobile skeleton */}
                  <div className="md:hidden space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="flex gap-2">
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                      <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Error state */}
          {error && <div className="text-sm text-red-600 mb-6">{error}</div>}
          
          {/* Referrals table */}
          {!loading && !completionLoading && !error && (
            <>
              {displayRows.length === 0 ? (
                <EmptyState tab={activeTab} />
              ) : (
                <ReferralsTable
                  rows={displayRows}
                  orgId={orgId}
                  refTypeByPatientId={refTypeByPatientId}
                  getRoleStatus={getRoleStatus}
                  userRole={userRole}
                  navigate={navigate}
                  completionData={completionData}
                  isCompletedTab={activeTab === 'completed'}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>      
  );
}

/**
 * Empty state component shown when a tab has no referrals
 */
function EmptyState({ tab }) {
  const message = tab === 'pending'
    ? 'No pending referrals. All your referrals are complete!'
    : 'No completed referrals yet.';
  
  return (
    <div className="flex items-center gap-3 px-4 py-6 rounded-xl border border-gray-200 bg-gray-50 text-gray-600">
      <InformationCircleIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  );
}

/**
 * Status badge component - displays form completion status with appropriate styling
 */
function StatusBadge({ status }) {
  const { label, className } = getStatusBadgeConfig(status);
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

/**
 * Referrals table component - displays list of referrals with School/Guardian status columns
 * Shows "(You)" indicator next to the column matching the current user's role
 * 
 * For guardians in the "Completed" tab: Shows triage forms progress if assigned but not complete,
 * helping guardians understand there may be additional forms to complete.
 */
function ReferralsTable({ rows, orgId, refTypeByPatientId, getRoleStatus, userRole, navigate, completionData, isCompletedTab }) {
  // Determine column labels based on who is logged in
  const schoolLabel = userRole === 'school_user' ? 'School Rep. (You)' : 'School';
  const parentLabel = userRole === 'guardian' ? 'Parent / Carer (You)' : 'Parent / Carer';
  
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-visible">
      {/* Table header - responsive: hide some columns on mobile */}
      <div className="hidden md:grid grid-cols-12 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
        <div className="col-span-3">Name</div>
        <div className="col-span-2">{schoolLabel}</div>
        <div className="col-span-2">{parentLabel}</div>
        <div className="col-span-2">Referred on</div>
        <div className="col-span-3 text-right">Actions</div>
      </div>
      
      {/* Mobile header */}
      <div className="md:hidden px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
        Your Referrals
      </div>
      
      {/* Table rows */}
      <div>
        {rows.map((r) => {
          const patientName =
            r.patientName ||
            (r.patient && (r.patient.fullName || [r.patient.firstName, r.patient.lastName].filter(Boolean).join(' '))) ||
            r.patientRecordId;
          const patientRecordId = r.patientRecordId;

          // Pending RMS participant (if any) the current user can send a reminder to.
          const cd = completionData[patientRecordId] || {};
          const reminderTarget = cd.reminderTargetId
            ? { id: cd.reminderTargetId, role: cd.reminderTargetRole }
            : null;

          // Get status for each role
          const schoolStatus = getRoleStatus(patientRecordId, 'school_user');
          const guardianStatus = getRoleStatus(patientRecordId, 'guardian');
          
          // For guardians in completed tab: check if triage forms are assigned and not complete
          // This helps guardians understand there may be additional forms to fill out
          const patientData = completionData?.[patientRecordId];
          const triageAssigned = patientData?.triageAssigned || false;
          const formProgress = patientData?.formProgress || {};
          
          // Calculate triage progress regardless of triageAssigned flag
          // (the flag is set when isTriageFormInstanceKey matches, but we can also
          // check directly if any triage forms have progress > 0)
          const triageProgress = getTriageFormsProgress(formProgress);
          const hasAnyTriageForms = triageAssigned || triageProgress.completed > 0;
          const hasIncompleteTriageForms = hasAnyTriageForms && triageProgress.completed < triageProgress.total;
          const showTriageIndicator = isCompletedTab && userRole === 'guardian' && hasIncompleteTriageForms;
          
          return (
            <div key={patientRecordId} className="border-t border-gray-100">
              {/* Desktop layout */}
              <div className="hidden md:grid grid-cols-12 px-3 py-3 items-center">
                <div className="col-span-3 text-sm text-gray-800 font-medium truncate pr-2">
                  {patientName}
                  {/* Triage forms indicator for guardians with incomplete additional forms */}
                  {showTriageIndicator && (
                    <div className="mt-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs border border-amber-200">
                        <ClipboardCopyIcon className="w-3 h-3" />
                        Additional Forms: {triageProgress.completed}/{triageProgress.total}
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <StatusBadge status={schoolStatus} />
                </div>
                <div className="col-span-2">
                  <StatusBadge status={guardianStatus} />
                </div>
                <div className="col-span-2 flex items-center gap-2 text-sm text-gray-600">
                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                  {formatDateShort(r.createdAt, '-')}
                </div>
                <div className="col-span-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                      onClick={() => navigate(`/rms/patient/${encodeURIComponent(patientRecordId)}/overview`)}
                    >
                      View referral
                    </button>
                    <RowOverflowMenu patientRecordId={patientRecordId} orgId={orgId} reminderTarget={reminderTarget} />
                  </div>
                </div>
              </div>
              
              {/* Mobile layout - stacked cards */}
              <div className="md:hidden px-3 py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-800 font-medium truncate flex-1 mr-2">
                    {patientName}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                      onClick={() => navigate(`/rms/patient/${encodeURIComponent(patientRecordId)}/overview`)}
                    >
                      View Referral
                    </button>
                    <RowOverflowMenu patientRecordId={patientRecordId} orgId={orgId} reminderTarget={reminderTarget} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-gray-500">{userRole === 'school_user' ? 'School (You):' : 'School:'}</span>
                  <StatusBadge status={schoolStatus} />
                  <span className="text-gray-500 ml-2">{userRole === 'guardian' ? 'Parent / Carer (You):' : 'Parent / Carer:'}</span>
                  <StatusBadge status={guardianStatus} />
                </div>
                {/* Triage forms indicator for guardians (mobile) */}
                {showTriageIndicator && (
                  <div className="flex items-center gap-1 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      <ClipboardCopyIcon className="w-3 h-3" />
                      Additional Forms: {triageProgress.completed}/{triageProgress.total}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>Referred:</span>
                  <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                  {formatDateShort(r.createdAt, '-')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Overflow menu for each referral row with additional actions
 * 
 * This menu opens on hover rather than click for better UX - users can naturally
 * move their mouse away to close the menu instead of having to click the button again.
 * The entire menu container (button + dropdown) is a single hover zone, so users can
 * move their mouse from the button to the dropdown items without the menu closing.
 * 
 * @param {string} patientRecordId - The CYP/patient record ID
 * @param {string} orgId - RMS organisation ID
 * @param {{id: string, role: string}|null} reminderTarget - The pending RMS
 *   participant (guardian or school_user) to resend an invite to, or null when
 *   there is no still-pending participant to remind.
 */
function RowOverflowMenu({ patientRecordId, orgId, reminderTarget }) {
  const [open, setOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  // Hover handlers for showing/hiding the dropdown
  // The menu opens when mouse enters the container and closes when mouse leaves
  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  const copyReferralId = async () => {
    try {
      await navigator.clipboard.writeText(patientRecordId);
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = patientRecordId;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) {
        // soft-fail
      }
    } finally {
      setOpen(false);
    }
  };

  // Resend the invite email to the pending RMS participant (TD-16). Shown only
  // when there is a still-pending other-party participant to nudge. The label
  // reflects which RMS role is being reminded — the RMS Legal Guardian
  // ("Parent / Carer") or the RMS School Representative.
  const reminderLabel = reminderTarget?.role === PARTICIPANT_ROLE.GUARDIAN
    ? 'Send reminder to Parent / Carer'
    : 'Send reminder to School Rep.';

  const handleSendReminder = async () => {
    if (!reminderTarget?.id || !orgId || sending) return;
    setSending(true);
    try {
      await apiResendParticipantInvite({ orgId, patientRecordId, participantId: reminderTarget.id });
      toast.success('Reminder email sent');
    } catch (e) {
      console.error('[ServiceUserDashboard] Failed to resend invite:', e);
      toast.error('Could not send the reminder. Please try again.');
    } finally {
      setSending(false);
      setOpen(false);
    }
  };

  return (
    <div 
      className="relative inline-block text-left"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        aria-label="More actions"
        aria-expanded={open}
        aria-haspopup="true"
        className="p-1 rounded-md hover:bg-gray-100"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-600">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {/* 
        Dropdown container with invisible bridge:
        - The outer div starts immediately below the button (no margin gap)
        - pt-2 adds visual spacing via padding instead of margin
        - This keeps the hover zone continuous so users can move from button to menu
        - z-50 on outer div ensures proper stacking above other table rows
      */}
      <div className={`absolute right-0 top-full pt-2 z-50 ${open ? '' : 'pointer-events-none'}`}>
        <div className={`w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all duration-150 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}>
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            onClick={copyReferralId}
          >
            <ClipboardCopyIcon className="w-4 h-4 text-gray-500" />
            Copy CYP ID
          </button>
          {reminderTarget?.id && (
            <button
              type="button"
              disabled={sending}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 flex items-center gap-2 disabled:opacity-50"
              onClick={handleSendReminder}
            >
              <MailIcon className="w-4 h-4 text-gray-500" />
              {sending ? 'Sending…' : reminderLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
